import { useCallback, useMemo, useState } from "react";
import type { HistoryFilter } from "@common/types/auditLog";
import type { DensityChartWindow } from "@design-system/DensityChart/DensityChart.lib";
import type { DensityChartBucketUnit } from "@design-system/DensityChart/densityBuckets.lib";
import { getViewerTimeZone } from "@helpers/date";
import { toTimelineChunks } from "./activityTimelineChunks.lib";
import type { TimelinePerson } from "./activityTimelinePeople.lib";
import {
    toTimelineBreakdown,
    toTimelinePeople,
} from "./activityTimelinePeople.lib";
import { toTimelineCountsFilter } from "./activityTimelinePicks.lib";
import {
    toCreatedAtBounds,
    toTimelineBucket,
} from "./activityTimelineSeries.lib";

interface UseActivityTimelineCountsParamsOptions<Filter extends HistoryFilter> {
    /** The History list's filter; the counts swap its dates for each chunk's. */
    filter: Filter;
    unit: DensityChartBucketUnit;
    /** Everyone Updated by can pick, to chart picked people by name. */
    knownPeople: readonly TimelinePerson[];
    today: string;
}

interface Visit {
    /** The unit, today and filter the chunks were visited under. */
    key: string;
    /** The unit and days the chart last drew. */
    unit: DensityChartBucketUnit;
    drawnDays: DensityChartWindow;
    chunks: readonly DensityChartWindow[];
}

const NO_CHUNKS: readonly DensityChartWindow[] = [];

const toChunkKey = ({ start, end }: DensityChartWindow) => `${start}/${end}`;

/** `visited` with any of `next` it doesn't hold yet after it, or `visited` itself when there are none. */
function addChunks(
    visited: readonly DensityChartWindow[],
    next: readonly DensityChartWindow[],
): readonly DensityChartWindow[] {
    const visitedKeys = new Set(visited.map(toChunkKey));
    const added = next.filter((chunk) => !visitedKeys.has(toChunkKey(chunk)));
    return added.length > 0 ? [...visited, ...added] : visited;
}

/** The visit's chunks, or once today or the filter changes those covering the days it last drew, in visit order; none until the chart draws `unit`. */
function toVisitChunks({
    visit,
    key,
    unit,
    today,
}: {
    visit: Visit | undefined;
    key: string;
    unit: DensityChartBucketUnit;
    today: string;
}): readonly DensityChartWindow[] {
    if (visit?.unit !== unit) {
        return NO_CHUNKS;
    }
    if (visit.key === key) {
        return visit.chunks;
    }
    const inView = toTimelineChunks({
        drawnDays: visit.drawnDays,
        unit,
        today,
    });
    const inViewKeys = new Set(inView.map(toChunkKey));
    return addChunks(
        visit.chunks.filter((chunk) => inViewKeys.has(toChunkKey(chunk))),
        inView,
    );
}

/**
 * The people a timeline charts one by one and a counts query per chunk the
 * drawn bars have touched since the unit, today or the counted filter's
 * non-date parts last changed, so the bars scale to all of them: the list's
 * filter (less whole series picked alone, `toTimelineCountsFilter`) in the
 * unit's bucket and the viewer's time zone, split by person when there are
 * people to chart. A change of today or the filter keeps only the chunks in
 * view. Nothing loads until the chart reports the days it draws in the
 * current unit.
 */
export function useActivityTimelineCountsParams<Filter extends HistoryFilter>({
    filter,
    unit,
    knownPeople,
    today,
}: UseActivityTimelineCountsParamsOptions<Filter>) {
    const countsFilter = useMemo(
        () => toTimelineCountsFilter(filter),
        [filter],
    );
    const filterKey = JSON.stringify({
        ...countsFilter,
        minCreatedAt: undefined,
        maxCreatedAt: undefined,
    });
    const key = `${unit}/${today}/${filterKey}`;
    const [visit, setVisit] = useState<Visit>();
    const onDrawnDaysChange = useCallback(
        (drawnDays: DensityChartWindow) =>
            setVisit((current) => {
                const chunks = addChunks(
                    toVisitChunks({ visit: current, key, unit, today }),
                    toTimelineChunks({ drawnDays, unit, today }),
                );
                const isSameVisit =
                    current?.key === key &&
                    current.chunks === chunks &&
                    toChunkKey(current.drawnDays) === toChunkKey(drawnDays);
                return isSameVisit ? current : { key, unit, drawnDays, chunks };
            }),
        [key, unit, today],
    );
    const people = useMemo(
        () =>
            toTimelinePeople({
                actorIds: filter.actorIds,
                series: filter.series,
                members: knownPeople,
            }),
        [filter.actorIds, filter.series, knownPeople],
    );
    const chunks = useMemo(
        () => toVisitChunks({ visit, key, unit, today }),
        [visit, key, unit, today],
    );
    const queries = useMemo(
        () =>
            chunks.map((chunk) => ({
                filters: { ...countsFilter, ...toCreatedAtBounds(chunk) },
                bucket: toTimelineBucket(unit),
                timeZone: getViewerTimeZone(),
                breakdown: toTimelineBreakdown(people),
            })),
        [chunks, countsFilter, unit, people],
    );
    return { people, chunks, queries, onDrawnDaysChange };
}
